import { useGetAllowedSourceTypes, useGetSyncs, useProvisionWorkspace } from 'api/HightouchAPI';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { ReactNode, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useModalContext } from 'utils/contexts/modal';
import { CROSS_ORIGIN_EVENTS, DEMO_SOURCE, HIGHTOUCH_ORIGIN } from '../constants';
import { Source } from '../types';
import { DataQueryContext } from './DataQueryContext';

const { createSourceFinished, createSyncFinished, windowClosed } = CROSS_ORIGIN_EVENTS;

export const DataQueryContextProvider = ({ children }: { children: ReactNode }) => {
  const { hideModal } = useModalContext();
  const dispatch = useDispatch();
  /**
   * Provision workspace
   * Should provision a workspace on first visit
   * Each subsequent visit should return a string and can be ignored
   */
  const { data: workspaceResponse, mutate: provisionWorkspace } = useProvisionWorkspace({
    onError: (error) => {
      dispatch(
        addNegativeToastWithErrorId(
          'There was an error provisioning your workspace',
          'useProvisionWorkspace',
          error,
        ),
      );
    },
  });

  useEffect(() => {
    if (!workspaceResponse) {
      provisionWorkspace({});
    }
  }, [workspaceResponse]);

  /**
   * Catalog
   * Retreives list of allowed source types to display in catalog section
   */
  const { data, isLoading: isLoadingAllowedSourceTypes } = useGetAllowedSourceTypes({
    onError: (error) => {
      dispatch(
        addNegativeToastWithErrorId(
          'There was an error fetching the allowed source types',
          'useGetAllowedSourceTypes',
          error,
        ),
      );
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
  const allowedSourceTypes = useMemo(() => data?.allowedSourceTypes ?? [], [data]);

  /**
   * Syncs
   * List of syncs grouped by source
   */
  const {
    data: { data: syncs = [] } = {},
    isError,
    isLoading: isLoadingSyncs,
    refetch: refetchSyncs,
  } = useGetSyncs({
    enabled: !!workspaceResponse,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error) => {
      dispatch(
        addNegativeToastWithErrorId('There was an error fetching your syncs', 'useGetSyncs', error),
      );
    },
  });

  /**
   * Events
   * Hightouch provided events to manage embedded workflow
   */
  useEffect(() => {
    const handleMessage = ({ data: messageData, origin }: MessageEvent) => {
      if (origin !== HIGHTOUCH_ORIGIN) {
        return null;
      }

      if (messageData?.message === createSyncFinished) {
        refetchSyncs();
        hideModal();
        dispatch(addPositiveToast('Your sync was created successfully'));
      }

      if (messageData?.message === createSourceFinished) {
        refetchSyncs();
        hideModal();
        dispatch(addPositiveToast('Your source was connected successfully'));
      }

      if (messageData?.message === windowClosed) {
        refetchSyncs();
        // add generic positive toast message message?
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  /**
   * Filter out syncs that are not currently allowed, as well as the demo source
   * provided in the API response
   */
  const formattedSyncs = useMemo(
    () =>
      syncs.filter((source: Source) => {
        const isAllowed = allowedSourceTypes.includes(source.type);
        const isDemo = source.sourceId === DEMO_SOURCE.sourceId;
        return isAllowed && !isDemo;
      }),
    [syncs, allowedSourceTypes],
  );

  const value = {
    allowedSourceTypes,
    isError,
    isLoading: isLoadingAllowedSourceTypes || isLoadingSyncs,
    refetchSyncs,
    syncs: formattedSyncs,
  };

  return <DataQueryContext.Provider value={value}>{children}</DataQueryContext.Provider>;
};
